import React, { Fragment } from 'react';
import { TabbedShowLayout, TabbedShowLayoutTabs, Tab, usePermissions } from 'react-admin';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import { useShowController } from 'ra-core';
import SolutionIcon from '@material-ui/icons/Star';
import { Show } from '~/layout';
import { NoPermissions } from '~/components/ra';
import { UpdateContext } from '~/components';
import { useHandbook } from '~/hooks';
import {
  ProductTab,
  SummaryTab,
  PricingTab,
  FXMappingTab,
  FeaturesConfigTab,
  NotificationsTab,
  HandbookTab,
  PIAvailabilityTab,
  LegalConsentsTab,
} from './components/Tabs';
import { Solution } from '~/types';

const useStyles = makeStyles({
  tabsGrid: {
    display: 'grid',
  },
});

const ShrinkableTabbedShowLayout = ({ children, ...rest }: any) => {
  const classes = useStyles();
  return (
    <div className={classes.tabsGrid}>
      <TabbedShowLayout
        style={{ marginBottom: 32, minWidth: 0 }}
        tabs={<TabbedShowLayoutTabs variant='scrollable' scrollButtons='auto' />}
        {...rest}
      >
        {children}
      </TabbedShowLayout>
    </div>
  );
};

const SolutionShow = (props: any) => {
  const { permissions, basePath, resource } = usePermissions();
  const showController = useShowController(props);
  const showRecord = showController?.record as Solution;

  useHandbook(showRecord?.id);

  if (!permissions) return null;
  if (!permissions?.includes('solution.view')) return <NoPermissions />;

  return (
    <Fragment>
      {showRecord && <UpdateContext data={{ solutionId: showRecord.id }} />}
      <Show
        icon={
          <Avatar>
            <SolutionIcon />
          </Avatar>
        }
        {...props}
      >
        <ShrinkableTabbedShowLayout>
          <Tab label='resources.solutions.tabs.summary'>
            <SummaryTab permissions={permissions} resource={'solution'} record={showRecord} />
          </Tab>
          {permissions?.includes('solution.product.list') &&
            permissions?.includes('solution.product.view') && (
              <Tab label='resources.solutions.tabs.products' path='products'>
                <ProductTab permissions={permissions} />
              </Tab>
            )}
          {permissions?.includes('solution.pricing.list') && (
            <Tab label='resources.solutions.tabs.pricingTables' path='pricingTables'>
              <PricingTab permissions={permissions} />
            </Tab>
          )}
          {permissions?.includes('solution.settings.view') && (
            <Tab label='resources.solutions.tabs.featuresConfig' path='featuresConfig'>
              <FeaturesConfigTab record={showRecord} basePath={basePath} resource={resource} />
            </Tab>
          )}
          {permissions?.includes('fxrates.mapping.list') && (
            <Tab label='resources.solutions.tabs.fxPlans' path='fxPlans'>
              <FXMappingTab permissions={permissions} />
            </Tab>
          )}
          {permissions?.includes('solution.handbook.view') && (
            <Tab label='resources.solutions.tabs.handbooks' path='handbooks'>
              <HandbookTab solutionId={props.id} />
            </Tab>
          )}
          {permissions?.includes('solution.settings.notification.view') && (
            <Tab label='resources.solutions.tabs.notifications' path='notifications'>
              <NotificationsTab
                permissions={permissions}
                solutionId={props.id}
                features={showRecord?.features}
              />
            </Tab>
          )}
          {permissions?.includes('solution.paymentInstrumentAvailabilityRules.list') && (
            <Tab label='resources.solutions.tabs.piAvailability' path='piAvailability'>
              <PIAvailabilityTab permissions={permissions} record={showRecord} />
            </Tab>
          )}
          {permissions?.includes('solution.legalConsent.list') && (
            <Tab label='resources.solutions.tabs.legalConsents' path='legalConsents'>
              <LegalConsentsTab permissions={permissions} />
            </Tab>
          )}
        </ShrinkableTabbedShowLayout>
      </Show>
    </Fragment>
  );
};

export default SolutionShow;
